import { useEffect, useRef, useState } from 'react'
import { ChevronDown } from 'lucide-react'
import { api, ApiRequestError } from '../api'
import type { PublishingPlan, SproutDestinations } from '../types'
import { CaptionGenerateButton } from './CaptionGenerateButton'
import { Button } from './ui'
import './PostComposer.css'

const blank: PublishingPlan = { revision: 0, caption: '', hashtags: '', link: '', location: '', tags: '',
  collaborators: '', altText: '', scheduled_time: '', media_version_id: null, customer_id: null,
  group_id: null, profile_ids: [], tag_ids: [] }
const labels: Record<string, string> = { instagram: 'Instagram', tiktok: 'TikTok', youtube: 'YouTube',
  linkedin: 'LinkedIn', facebook: 'Facebook', twitter: 'X', threads: 'Threads', pinterest: 'Pinterest' }

export function normalizePost(plan?: Partial<PublishingPlan>): PublishingPlan {
  const combined = { ...blank, ...plan }
  const value = { ...blank, ...Object.fromEntries(Object.keys(blank).map(key => [key, combined[key as keyof PublishingPlan]])) }
  const words = new Set(value.caption.toLowerCase().split(/\s+/))
  const oldTags = value.hashtags.split(/\s+/).filter(tag => {
    if (!tag || words.has(tag.toLowerCase())) return false
    words.add(tag.toLowerCase()); return true
  })
  return { ...value, caption: [value.caption, oldTags.join(' ')].filter(Boolean).join('\n\n'),
    hashtags: '', link: '', location: '', tags: '', collaborators: '', altText: '', scheduled_time: '', tag_ids: [] }
}

const hasLegacyDetails = (plan?: Partial<PublishingPlan>) => Boolean(plan &&
  (plan.hashtags || plan.link || plan.location || plan.tags || plan.collaborators || plan.altText || plan.scheduled_time || plan.tag_ids?.length))

export function PostComposer({ identity, plan, mediaType = 'VIDEO', sendBlocked = false, canSend = true, previousJobId = null, hasDelivered = false, save, send, generate, reload }: {
  identity: string; plan?: Partial<PublishingPlan>; mediaType?: 'PHOTO' | 'VIDEO'; sendBlocked?: boolean; canSend?: boolean
  previousJobId?: string | null; hasDelivered?: boolean
  save: (value: PublishingPlan) => Promise<PublishingPlan>
  send: (revision: number, requestId: string, previousJobId: string | null) => Promise<unknown>
  generate: () => Promise<{ caption: string }>
  reload: () => Promise<PublishingPlan>
}) {
  const [draft, setDraft] = useState(() => normalizePost(plan))
  const draftRef = useRef(draft)
  const revision = useRef(draft.revision)
  const changed = useRef(hasLegacyDetails(plan) ? 1 : 0)
  const captionChanges = useRef(0)
  const saved = useRef(0)
  const active = useRef(true)
  const pending = useRef<Promise<void> | null>(null)
  const [catalog, setCatalog] = useState<SproutDestinations | null>(null)
  const [saving, setSaving] = useState(false)
  const [sending, setSending] = useState(false)
  const sendInFlight = useRef(false)
  const sendRequest = useRef<{ revision: number; previousJobId: string | null; id: string } | null>(null)
  const [generating, setGenerating] = useState(false)
  const [error, setError] = useState('')
  const [conflict, setConflict] = useState(false)
  const [catalogError, setCatalogError] = useState('')
  const [catalogAttempt, setCatalogAttempt] = useState(0)
  const [dirty, setDirty] = useState(hasLegacyDetails(plan))
  const callbacks = useRef({ save, send, generate, reload })
  callbacks.current = { save, send, generate, reload }
  function update(patch: Partial<PublishingPlan>) {
    if (patch.caption !== undefined) captionChanges.current += 1
    changed.current += 1
    draftRef.current = { ...draftRef.current, ...patch }
    setDraft(draftRef.current); setDirty(true); setError('')
  }
  useEffect(() => {
    active.current = true
    let cancelled = false
    setCatalogError('')
    void api.sproutDestinations().then(value => {
      if (cancelled) return
      setCatalog(value)
      const eligible = value.profiles.filter(p => mediaType !== 'PHOTO' || p.supports_photo).map(p => p.id)
      const current = draftRef.current
      const ids = current.customer_id == null ? eligible : current.profile_ids.filter(id => eligible.includes(id))
      const patch = { customer_id: value.customer_id, group_id: value.group_id, profile_ids: ids }
      if (current.customer_id !== patch.customer_id || current.group_id !== patch.group_id
        || JSON.stringify(current.profile_ids) !== JSON.stringify(ids) || hasLegacyDetails(plan) || !current.revision) update(patch)
    }).catch(() => { if (!cancelled) setCatalogError('Accounts could not be loaded. You can keep editing and saving your post.') })
    return () => {
      cancelled = true; active.current = false
      if (!conflictRef.current) void persist().catch(() => {})
    }
  }, [identity, mediaType, catalogAttempt])
  useEffect(() => {
    if (!plan?.revision || plan.revision <= revision.current || pending.current || changed.current !== saved.current) return
    const value = normalizePost(plan)
    if (value.caption !== draftRef.current.caption) captionChanges.current += 1
    draftRef.current = value; revision.current = value.revision
    setDraft(value)
    if (hasLegacyDetails(plan)) { changed.current += 1; setDirty(true) }
  }, [plan?.revision])
  async function persist(): Promise<void> {
    if (pending.current) return pending.current
    if (changed.current === saved.current) return
    if (active.current) setSaving(true)
    const work = (async () => {
      while (changed.current !== saved.current) {
        const sequence = changed.current
        const snapshot = { ...normalizePost(draftRef.current), revision: revision.current }
        const result = await callbacks.current.save(snapshot)
        revision.current = result.revision
        saved.current = sequence
        if (active.current) setDirty(changed.current !== saved.current)
      }
    })().catch(reason => {
      if (active.current) {
        setError(reason instanceof Error ? reason.message : 'Post details could not be saved.')
        if (reason instanceof ApiRequestError && reason.status === 409) {
          conflictRef.current = true; setConflict(true)
        }
      }
      throw reason
    }).finally(() => { pending.current = null; if (active.current) setSaving(false) })
    pending.current = work
    await work
  }
  useEffect(() => {
    if (!dirty || saving || sending || generating || error || conflict) return
    const timer = window.setTimeout(() => { void persist().catch(() => {}) }, 600)
    return () => window.clearTimeout(timer)
  }, [draft, dirty, saving, sending, generating, error, conflict])
  async function schedule() {
    if (!catalog || !canSend || sendBlocked || sendInFlight.current || generating || conflict || !draftRef.current.profile_ids.length) return
    sendInFlight.current = true
    setSending(true); setError('')
    try {
      await persist()
      // Edits made while the first autosave was in flight need their own revision.
      if (changed.current !== saved.current) await persist()
      if (!active.current || changed.current !== saved.current || !revision.current) return
      if (!sendRequest.current || sendRequest.current.revision !== revision.current || sendRequest.current.previousJobId !== previousJobId) {
        sendRequest.current = { revision: revision.current, previousJobId, id: crypto.randomUUID() }
      }
      await callbacks.current.send(revision.current, sendRequest.current.id, previousJobId)
    } catch (reason) { if (active.current) setError(reason instanceof Error ? reason.message : 'The Sprout draft could not be scheduled.') }
    finally { sendInFlight.current = false; if (active.current) setSending(false) }
  }
  async function suggest() {
    const started = captionChanges.current
    setGenerating(true); setError('')
    try {
      const value = await callbacks.current.generate()
      if (active.current && started === captionChanges.current) update({ caption: value.caption })
      else if (active.current) setError('Your caption changed while generating. Your edits have been kept.')
    }
    catch (reason) { if (active.current) setError(reason instanceof Error ? reason.message : 'Caption generation failed. Try again.') }
    finally { if (active.current) setGenerating(false) }
  }
  const conflictRef = useRef(false)
  async function reloadSaved() {
    try {
      const shared = await callbacks.current.reload()
      const value = normalizePost(shared)
      if (!active.current) return
      captionChanges.current += 1
      draftRef.current = value; revision.current = value.revision
      changed.current = hasLegacyDetails(shared) ? 1 : 0; saved.current = 0
      setDraft(value); setDirty(hasLegacyDetails(shared)); setError(''); setConflict(false); conflictRef.current = false
    } catch (reason) { if (active.current) setError(reason instanceof Error ? reason.message : 'Saved details could not be loaded.') }
  }
  const selectedNames = catalog?.profiles.filter(profile => draft.profile_ids.includes(profile.id))
    .map(profile => labels[profile.network] || profile.network) || []
  return <div className="post-composer">
    <fieldset disabled={sending}>
      <div className="caption-composer">
        <label className="post-caption"><span>Caption</span><textarea aria-label="Caption" value={draft.caption} rows={5}
          maxLength={6002} placeholder="Write your caption, including any #hashtags…" onChange={event => update({ caption: event.target.value })} /></label>
        <CaptionGenerateButton busy={generating} disabled={sending} onClick={() => void suggest()} />
      </div>
      <div className="post-destinations"><span className="post-field-label">Destinations</span>
        {!catalog && !catalogError ? <span className="post-loading" role="status" aria-label="Loading destinations"><span className="post-spinner" aria-hidden="true" /></span> : null}
        {catalog ? <details className="post-destination-menu" onKeyDown={event => {
          if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); event.currentTarget.open = false; event.currentTarget.querySelector('summary')?.focus() }
        }} onBlur={event => { if (!event.currentTarget.contains(event.relatedTarget)) event.currentTarget.open = false }}>
          <summary aria-label="Choose destinations"><span>{selectedNames.length ? selectedNames.join(', ') : 'Choose destinations'}</span><ChevronDown size={14} aria-hidden="true" /></summary>
          <div className="post-destination-options">
            {catalog.profiles.filter(profile => mediaType !== 'PHOTO' || profile.supports_photo).map(profile => {
              const label = `${labels[profile.network] || profile.network} · ${profile.name}`
              return <label key={profile.id} className="post-destination">
                <input type="checkbox" aria-label={label} checked={draft.profile_ids.includes(profile.id)}
                  onChange={() => {
                    const selected = new Set(draftRef.current.profile_ids)
                    if (selected.has(profile.id)) selected.delete(profile.id)
                    else selected.add(profile.id)
                    update({ profile_ids: [...selected].sort((a, b) => a - b) })
                  }} />
                <span>{labels[profile.network] || profile.network}<small>{profile.name}</small></span>
              </label>
            })}
          </div>
        </details> : null}
      </div>
    </fieldset>
    <div className="post-composer-footer"><span>{saving ? <span className="post-spinner" role="status" aria-label="Saving caption" /> : null}</span>
      <Button type="button" busy={sending} disabled={!catalog || !canSend || sendBlocked || generating || sending || conflict || !draft.profile_ids.length}
        onClick={() => void schedule()}>{sending ? 'Scheduling…' : hasDelivered ? 'Schedule another draft' : 'Schedule to Sprout Social'}</Button>
    </div>
    {error ? <p className="post-composer-error" role="alert">{error}</p> : null}
    {catalogError ? <p className="post-composer-error" role="alert">{catalogError} <button type="button" onClick={() => setCatalogAttempt(value => value + 1)}>Reload accounts</button></p> : null}
    {conflict ? <div className="post-native-notice">Reloading replaces your unsaved changes with the shared post. <button type="button" onClick={() => void reloadSaved()}>Reload saved details</button></div> : null}
  </div>
}
